import React, { useState, useEffect } from 'react';
import { getCdnImage } from '../data/content';

export function Gallery({ gallerySlug, title, subtitle }) {
  const [galleryData, setGalleryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImageIndex, setActiveImageIndex] = useState(null);

  useEffect(() => {
    async function fetchGallery() {
      try {
        setLoading(true);
        // Fetch from public CMS content endpoint
        const response = await fetch('https://api.dev.auxo.dk/public/content/galleri');
        if (response.ok) {
          const items = await response.json();
          // Find item with matching gallery_slug, slug string or object
          const match = items.find(
            (item) =>
              item.gallery_slug === gallerySlug ||
              item.slug === gallerySlug ||
              (typeof item.slug === 'object' && item.slug?._ === gallerySlug) ||
              item.category?.toLowerCase() === gallerySlug?.toLowerCase()
          );
          if (match) {
            setGalleryData(match);
          } else if (items.length > 0) {
            // Fallback to first gallery if no exact slug match
            setGalleryData(items[0]);
          }
        }
      } catch (err) {
        console.error('Kunne ikke hente galleri fra CMS:', err);
      } finally {
        setLoading(false);
      }
    }

    if (gallerySlug) {
      fetchGallery();
    }
  }, [gallerySlug]);

  if (loading) {
    return (
      <div className="py-12 text-center text-slate-500">
        <i className="fa-solid fa-spinner fa-spin text-2xl text-red-600 mb-2" aria-hidden="true" />
        <p className="text-sm font-medium">Henter CMS galleri ({gallerySlug})...</p>
      </div>
    );
  }

  const images = galleryData?.images || [];
  const galleryTitle = title || galleryData?.title || 'Galleri';
  const galleryDesc = subtitle || galleryData?.description || '';

  if (images.length === 0) {
    return null;
  }

  return (
    <div className="w-full">
      {galleryDesc && (
        <div className="mb-6">
          <p className="text-slate-600 text-sm mt-1">{galleryDesc}</p>
        </div>
      )}

      {/* Touch-friendly Gallery Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {images.map((imgUrl, idx) => (
          <button
            key={idx}
            type="button"
            onClick={() => setActiveImageIndex(idx)}
            className="group relative h-64 w-full rounded-xl overflow-hidden bg-slate-900 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-red-500 cursor-pointer text-left transition-transform duration-200 hover:scale-[1.02] shadow-sm hover:shadow-md"
            aria-label={`Åbn billede ${idx + 1} i stor visning`}
          >
            <img
              src={getCdnImage(imgUrl, 600, 85)}
              alt={`${galleryTitle} - Billede ${idx + 1}`}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-slate-950/30 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
              <span className="bg-white/90 text-slate-900 text-xs font-bold px-3 py-1.5 rounded-full shadow flex items-center gap-1.5">
                <i className="fa-solid fa-magnifying-glass-plus text-red-600" aria-hidden="true" />
                Forstør
              </span>
            </div>
          </button>
        ))}
      </div>

      {/* Lightbox Modal */}
      {activeImageIndex !== null && (
        <div
          className="fixed inset-0 z-50 bg-slate-950/90 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={() => setActiveImageIndex(null)}
        >
          <div
            className="relative max-w-5xl w-full max-h-[90vh] flex flex-col items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              type="button"
              onClick={() => setActiveImageIndex(null)}
              className="absolute -top-12 right-0 text-white hover:text-red-400 text-2xl p-2 transition-colors focus:outline-none"
              aria-label="Luk stor visning"
            >
              <i className="fa-solid fa-xmark" aria-hidden="true" />
            </button>

            {/* Navigation prev/next */}
            {images.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={() =>
                    setActiveImageIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1))
                  }
                  className="absolute left-2 text-white bg-slate-900/80 hover:bg-red-600 p-3 rounded-full transition-colors z-10 focus:outline-none"
                  aria-label="Forrige billede"
                >
                  <i className="fa-solid fa-chevron-left" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setActiveImageIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0))
                  }
                  className="absolute right-2 text-white bg-slate-900/80 hover:bg-red-600 p-3 rounded-full transition-colors z-10 focus:outline-none"
                  aria-label="Næste billede"
                >
                  <i className="fa-solid fa-chevron-right" aria-hidden="true" />
                </button>
              </>
            )}

            <img
              src={getCdnImage(images[activeImageIndex], 1600, 90)}
              alt={`${galleryTitle} stor visning`}
              className="max-h-[80vh] max-w-full object-contain rounded-lg border border-slate-800 shadow-2xl"
            />

            <div className="mt-3 text-center text-slate-300 text-sm font-medium">
              Billede {activeImageIndex + 1} af {images.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
